import Button from '@vromo/ui/components/Button';
import { action, runInAction } from 'mobx';
import React, { useState, useEffect } from 'react';
import useViewModel from '@vromo/hooks/useViewModel';
import { serialize, update } from 'serializr';

import Modal, { ModalHeader, ModalContent, ModalActions } from '@vromo/ui/components/Modal';
import { useModals } from '@vromo/ui/components/Modals';
import { useToasts } from '@vromo/ui/components/Toasts';
import Alert from '@vromo/ui/components/Alert';
import Input from '@vromo/ui/components/Input';
import Title from '@vromo/ui/components/Title';

import BlueprintTask from './BlueprintTask';
import BlueprintTaskEditor from './BlueprintTaskEditor';

import { useStore } from '@vromo/mobx-react';
import { observer } from 'mobx-react-lite';

import { Blueprint, TaskBlueprint } from '@vromo/models';
import { EntityList } from '@vromo/models';

import NewBlueprintWizard from '@vromo/dashboard/src/blueprints/NewBlueprintWizard';
import {
    AddButtonContainer,
    FilterRow,
    Flex,
    GreyDivider,
    TwoPageModal
} from '@vromo/dashboard/src/blueprints/BlueprintStyles';
import BlueprintTaskSources from '@vromo/dashboard/src/blueprints/BlueprintTaskSources';
import { useTranslation } from 'react-i18next';

type Props = {
    open: boolean;
    close: () => void;
    blueprint: Blueprint;
    isNew: boolean;
};

export default observer(function BlueprintEditor({ open, close, blueprint, isNew }: Props) {
    const {
        authStore: { graph, role },
        blueprintStore
    } = useStore('rootStore');

    const [editedBlueprint, editedBlueprintPatch] = useViewModel(blueprint);
    const [selectedTask, setSelectedTask] = useState<number | null>(null);
    const [showAlert, setShowAlert] = useState<boolean>(false);

    const { t } = useTranslation();

    const isPickupAndDropoff =
        Math.max(...editedBlueprint.template.tasks.map((it) => it.source), 0) === 1;

    const modals = useModals();
    const showToast = useToasts();

    useEffect(() => {
        if (open) {
            setSelectedTask(null);
        }
    }, [open]);

    function deleteBlueprint() {
        graph.root
            .graph()
            .roles(role.id)
            .blueprints(blueprint.id)
            .delete()
            .then(() => {
                close();
                showToast(t(`blueprint.removedJobTemplate`, { templateName: blueprint.name }));
                blueprintStore.blueprints.remove((b: Blueprint) => b.id === blueprint.id);
            })
            .catch((e: Error) => {
                console.error(e);
                showToast(t('blueprint.errorDeleting'), 'error');
            });
    }

    async function confirmDelete() {
        if (await modals.confirm(t('blueprint.deleteJob'), t('blueprint.deleteJobConfirmation'))) {
            deleteBlueprint();
        }
    }

    async function confirmClose() {
        if (
            !isNew &&
            editedBlueprint.isDirty() &&
            (await modals.confirm(t('blueprint.unsavedChanges'), t('blueprint.leaveWithoutSaving')))
        ) {
            close();
            editedBlueprint.reset();
            setShowAlert(false);
        } else if (isNew || (!isNew && !editedBlueprint.isDirty())) {
            close();
            setShowAlert(false);
            editedBlueprint.reset();
        }
    }

    function isValidBlueprint(blueprint: Blueprint) {
        return blueprint.name?.length > 0;
    }

    function saveBlueprint(blueprint: Blueprint) {
        if (isValidBlueprint(blueprint)) {
            const serializedBlueprint: Blueprint = serialize(blueprint);

            delete serializedBlueprint.created;

            graph.root
                .graph()
                .roles(role.id)
                .blueprints()
                .post([serializedBlueprint])
                .then(({ items }: EntityList<Blueprint>) => {
                    const item_name: string = items[0].name;
                    update(blueprintStore.blueprints, { items, type: 'create' });
                    close();
                    showToast(
                        t(isNew ? 'blueprint.successfullyAdded' : 'blueprint.successfullyUpdated', {
                            item_name
                        }),
                        'success'
                    );
                })
                .catch((e: Error) => {
                    console.error(e);
                    showToast(t('blueprint.errorAdding'), 'error');
                });
        } else {
            setShowAlert(true);
        }
    }

    function removeTask(ix: number) {
        runInAction(() => update(editedBlueprint, editedBlueprint.template.tasks.splice(ix, 1)));
    }

    function addNewTask(newTask: TaskBlueprint) {
        runInAction(() =>
            update(editedBlueprint, {
                tasks: editedBlueprint.template.tasks.push(newTask)
            })
        );
    }

    return (
        <>
            <Modal isOpen={open && isNew} onRequestClose={confirmClose}>
                <NewBlueprintWizard
                    blueprint={blueprint}
                    open={open}
                    close={confirmClose}
                    onSave={saveBlueprint}
                />
            </Modal>
            <Modal isOpen={open && !isNew} onRequestClose={confirmClose}>
                <>
                    {selectedTask !== null ? (
                        <BlueprintTaskEditor
                            taskTemplate={
                                selectedTask < editedBlueprint.template.tasks.length
                                    ? editedBlueprint.template.tasks[selectedTask]!
                                    : new TaskBlueprint()
                            }
                            cancel={() => setSelectedTask(null)}
                            isNew={selectedTask >= editedBlueprint.template.tasks.length}
                            addTask={addNewTask}
                        />
                    ) : (
                        <>
                            <ModalHeader>
                                <Title>{t('blueprint.editJobTemplate')}</Title>
                                {showAlert ? (
                                    <Alert danger>{t('blueprint.alertValidName')}</Alert>
                                ) : null}
                            </ModalHeader>
                            <ModalContent>
                                <TwoPageModal>
                                    <Flex>
                                        <FilterRow
                                            style={{
                                                flexDirection: 'row',
                                                justifyContent: 'space-between'
                                            }}>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}>
                                                <label>{t('blueprint.id')}</label>
                                                <p style={{ margin: 0 }}>{editedBlueprint.id}</p>
                                            </div>
                                        </FilterRow>
                                        <FilterRow>
                                            <Input
                                                label="Template Name"
                                                value={editedBlueprint.name}
                                                onChange={action((e: any) =>
                                                    update(editedBlueprint, {
                                                        name: e.target.value
                                                    })
                                                )}
                                            />
                                        </FilterRow>
                                        <FilterRow>
                                            <label>{t('blueprint.inputTasks')}</label>
                                        </FilterRow>
                                        {isPickupAndDropoff && (
                                            <BlueprintTaskSources
                                                isNew={isNew}
                                                blueprint={editedBlueprint}
                                            />
                                        )}
                                    </Flex>
                                    <GreyDivider />
                                    <Flex>
                                        <label>{t('blueprint.outputTasks')}</label>
                                        {editedBlueprint.template.tasks.map((t, ix) => (
                                            <BlueprintTask
                                                key={t.id}
                                                index={ix}
                                                taskTemplate={t}
                                                selectTask={() => setSelectedTask(ix)}
                                                removeTask={() => removeTask(ix)}
                                            />
                                        ))}
                                        <div className={AddButtonContainer}>
                                            <Button
                                                primary
                                                onClick={() =>
                                                    setSelectedTask(
                                                        editedBlueprint.template.tasks.length
                                                    )
                                                }>
                                                {t('blueprint.addTask')}
                                            </Button>
                                        </div>
                                    </Flex>
                                </TwoPageModal>
                            </ModalContent>
                            <ModalActions>
                                <Button
                                    destructive
                                    style={{ marginRight: 'auto' }}
                                    onClick={confirmDelete}>
                                    {t('blueprint.deleteButton')}
                                </Button>
                                <Button onClick={confirmClose}>
                                    {t('blueprint.cancelButton')}
                                </Button>
                                <Button primary onClick={() => saveBlueprint(editedBlueprint)}>
                                    {t('blueprint.saveJobTemplate')}
                                </Button>
                            </ModalActions>
                        </>
                    )}
                </>
            </Modal>
        </>
    );
});
